import React, { useEffect, useMemo, useRef } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { createClient, getFaxTemplates } from './CustomerView.actions';
import Spinner from 'components/Spinner';
import { showNotification } from 'services/NotificationService';
import { useConfigForm } from '../configTool/useConfigForm';
import { validateClientCreateForm } from '../configTool/configFormValidators';
import { ClientFormSections } from '../configTool/ClientFormSections';

const EMPTY_CLIENT_FORM = {
  clientName: '',
  programName: '',
  clientNameAbbreviation: '',
  logoUrl: '',
  supportNumber: '',
  faxNumberInbound: '',
  hasCustomSwitches: false,
  isPrescriberEmailEnabled: false,
  isRxAdvocateEnabled: false,
  prescriberCallActivitiesEnabled: false,
  pharmacyCallActivitiesEnabled: false,
  useNvpSourcePricing: false,
  excludeStarSwitches: false,
  excludeStarDeprescriptions: false,
  polypharmacyEnabled: false,
  deprescriptionConfirmationEnabled: false,
  populationType: '',
  language: '',
  faxTemplate: '',
};

const CreateClientModal = ({ show, onHide, customerInfoUuid, customerName, onSuccess }) => {
  const dispatch = useDispatch();

  const {
    creatingClient,
    createClientError,
    faxTemplates,
    loadingFaxTemplates,
  } = useSelector((state) => state.customerView);

  const initialValues = useMemo(() => ({ ...EMPTY_CLIENT_FORM }), []);

  const {
    formState,
    errors,
    handleInputChange,
    handleCheckboxChange,
    resetForm,
    runValidation,
  } = useConfigForm({
    initialValues,
    validate: validateClientCreateForm,
  });

  const formInitializedRef = useRef(false);

  useEffect(() => {
    if (show) {
      dispatch(getFaxTemplates());
    } else {
      formInitializedRef.current = false;
    }
  }, [dispatch, show]);

  useEffect(() => {
    if (!show || loadingFaxTemplates || formInitializedRef.current) {
      return;
    }
    resetForm({ ...EMPTY_CLIENT_FORM });
    formInitializedRef.current = true;
  }, [show, loadingFaxTemplates, resetForm]);

  const handleSave = () => {
    if (!runValidation()) {
      return;
    }
    dispatch(createClient(customerInfoUuid, formState))
      .then((result) => {
        if (result.type === 'CREATE_CLIENT_SUCCESS') {
          showNotification({
            title: 'Client created successfully!',
            message: 'The client for this customer is created successfully',
            position: 'tr',
            type: 'success',
          });
          onSuccess();
          handleClose();
        } else {
          showNotification({
            title: 'Error creating client.',
            position: 'tr',
            type: 'error',
          });
        }
      });
  };

  const handleClose = () => {
    resetForm({ ...EMPTY_CLIENT_FORM });
    dispatch({ type: 'CREATE_CLIENT_FAILURE', payload: null });
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Create Client for {customerName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loadingFaxTemplates || !formState ? <Spinner /> : (
          <>
            {createClientError && <Alert variant="danger">An error occurred while creating the client.</Alert>}
            <Form>
              <ClientFormSections
                formState={formState}
                errors={errors}
                onInputChange={handleInputChange}
                onCheckboxChange={handleCheckboxChange}
                faxTemplates={faxTemplates}
                loadingFaxTemplates={loadingFaxTemplates}
                showValidation
                disabledFields={{ callActivities: true }}
              />
            </Form>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={creatingClient}>
          {creatingClient ? 'Creating...' : 'Create Client'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateClientModal;
